import React, { useContext, useState, useEffect } from "react";
import * as Yup from "yup";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import Flex from "../../../components/shared-styles/flexi";
import Input from "../../../components/form/input";
import { ToastContext } from "../../../services/contexts/toast-context";
import { formatErrors, handleInput, handleSubmit } from "../../../services/utils/processors";
import styled from "styled-components";
import { useGetRecruiterProfile } from "../../../services/api/recruiter-api";

const ProfileContainer = styled.div`
  min-height: calc(100vh - 3rem);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProfileCard = styled.div`
  background: var(--bg-card);
  color: var(--text-main);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  max-width: 500px;
  width: 100%;
  padding: 2rem;
`;

export default function RecruiterProfileCreate({ defaultData = null, loading = false, onSave, onBack }) {
  const toastRef = useContext(ToastContext);
  const { data: recruiterData, isLoading: recruiterLoading } = useGetRecruiterProfile();

  const [form, setForm] = useState({
    company_name: "",
    position: "",
    website: "",
  });

  const [formErrors, setFormErrors] = useState({});

  // Prefill form with recruiter profile data
  useEffect(() => {
    if (recruiterData) {
      setForm({
        company_name: recruiterData.company_name || "",
        position: recruiterData.position || "",
        website: recruiterData.website || "",
      });
    } else if (defaultData) {
      setForm({
        company_name: defaultData.company_name || "",
        position: defaultData.position || "",
        website: defaultData.website || "",
      });
    }
  }, [recruiterData, defaultData]);

  const schema = Yup.object({
    company_name: Yup.string().required("Company Name is required"),
    position: Yup.string(),
    website: Yup.string().nullable(),
  });

  const handleInputChange = (value, field) => {
    handleInput(value, field, setForm,setFormErrors,schema);
  };

  // Validate and call onSave if valid, else show errors
  const handleSubmitForm = () => {
    handleSubmit(form, schema, setFormErrors, () => {
      if (onSave) onSave(form);
    });

    if (Object.keys(formErrors).length > 0) {
      toastRef.current.show({
        severity: "error",
        summary: "Error",
        detail: formatErrors(formErrors),
        life: 6000,
      });
    }
  };

  return (
    <ProfileContainer>
      {recruiterLoading ? (
        <ProgressSpinner style={{ width: '50px', height: '50px' }} />
      ) : (
        <ProfileCard>
            <h1 style={{ marginBottom: '1rem', fontSize: '1.5rem', fontWeight: '600' }}>
              {(recruiterData || defaultData) ? "Edit Recruiter Profile" : "Create Recruiter Profile"}
            </h1>
            <p style={{ marginBottom: '2rem', color: 'var(--text-secondary)' }}>
              {(recruiterData || defaultData) 
                ? "Update your recruiter information" 
                : "Complete your recruiter information to get started"
              }
            </p>
            
            <Flex direction="column" gap="1.5rem">
              <Input
                label="Company Name"
                value={form.company_name}
                onChange={(val) => handleInputChange(val, "company_name")}
                error={formErrors.company_name}
                placeholder="Enter company name"
                autoFocus
              />

              <Input
                label="Your Position"
                value={form.position}
                onChange={(val) => handleInputChange(val, "position")}
                error={formErrors.position}
                placeholder="e.g., Senior Recruiter"
              />

              <Input
                label="Website (optional)"
                value={form.website}
                onChange={(val) => handleInputChange(val, "website")}
                error={formErrors.website}
                placeholder="https://company.com"
              />
            </Flex>

            <Flex gap="1rem" justify="flex-end" style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
              {onBack && (
                <Button
                  label="Back"
                  icon="pi pi-arrow-left"
                  className="p-button-text p-button-secondary"
                  onClick={onBack}
                />
              )}
              <Button
                label={(recruiterData || defaultData) ? "Save Changes" : "Create Profile"}
                loading={loading}
                className="p-button-primary"
                onClick={handleSubmitForm}
              />
            </Flex>
        </ProfileCard>
      )}
    </ProfileContainer>
  );
}
